import { Box, Typography } from "@mui/material"
import { alpha, styled } from "@mui/material/styles"
import { Link, SvgIcon, type iconKeys } from ".."
import type { FC } from ".."
import StyledContainer from "./StyledContainer"
import { useRef, useEffect, useState } from "react"
import gsap from "gsap"

const SponsorsContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  overflow: "hidden",
  position: "relative",
  backgroundColor: theme.palette["tertiary.800"].main,
  borderTop: `1px solid ${alpha(theme.palette["neutral.700"].main, 0.2)}`,
}))

const SlidingTrack = styled(Box)(({ theme }) => ({
  display: "flex",
  width: "max-content",
}))

const Sponsors: FC = () => {
  const trackRef = useRef<HTMLDivElement>(null)
  const [trackWidth, setTrackWidth] = useState(0)
  const bronzeSponsors: { id: number; icon: iconKeys; url: string }[] = [
    { id: 1, icon: "feelNewSydney", url: "https://www.visitnsw.com/" },
  ]
  const goldSponsors: { id: number; icon: iconKeys; url: string }[] = [
    { id: 1, icon: "amazingThailand", url: "https://www.tourismthailand.org" },
  ]

  const platinumSponsors: { id: number; icon: iconKeys; url: string }[] = [
    { id: 1, icon: "turkishAirlines", url: "https://www.turkishairlines.com/" },
  ]

  // Measure the actual width of the track after render
  useEffect(() => {
    if (trackRef.current) {
      const width = trackRef.current.offsetWidth / 2
      setTrackWidth(width)
    }
  }, [])

  useEffect(() => {
    if (!trackRef.current || trackWidth === 0) return

    const track = trackRef.current
    const duration = 15 // seconds

    // Create the animation
    const animation = gsap.to(track, {
      x: -trackWidth,
      duration: duration,
      ease: "none",
      repeat: -1,
      onRepeat: () => {
        // Reset position when the first set of items is out of view
        gsap.set(track, { x: 0 })
      },
    })

    // Pause animation on hover
    const handleMouseEnter = () => animation.pause()
    const handleMouseLeave = () => animation.play()

    track.addEventListener("mouseenter", handleMouseEnter)
    track.addEventListener("mouseleave", handleMouseLeave)

    return () => {
      animation.kill()
      track.removeEventListener("mouseenter", handleMouseEnter)
      track.removeEventListener("mouseleave", handleMouseLeave)
    }
  }, [trackWidth])

  return (
    <SponsorsContainer>
      <StyledContainer
        maxWidth={1400}
        sx={{
          display: "flex",
          alignItems: "center",
          height: { xs: 150, md: 213 },
          padding: "0 !important",
        }}
      >
        <Box sx={{ position: "relative" }}>
          <SlidingTrack ref={trackRef}>
            <SponsorBox type="platinum" sponsors={platinumSponsors} />
            <SponsorBox type="gold" sponsors={goldSponsors} />
            <SponsorBox type="bronze" sponsors={bronzeSponsors} />
            {/* Duplicate boxes for seamless animation */}
            <SponsorBox type="platinum" sponsors={platinumSponsors} />
            <SponsorBox type="gold" sponsors={goldSponsors} />
            <SponsorBox type="bronze" sponsors={bronzeSponsors} />
            <SponsorBox type="platinum" sponsors={platinumSponsors} />
            <SponsorBox type="gold" sponsors={goldSponsors} />
            <SponsorBox type="bronze" sponsors={bronzeSponsors} />
          </SlidingTrack>
        </Box>
      </StyledContainer>
    </SponsorsContainer>
  )
}

export default Sponsors

// A sub component for a sponsor box
const SponsorBox = ({
  type,
  sponsors,
}: {
  type: "platinum" | "gold" | "bronze"
  sponsors: { id: number; icon: iconKeys; url: string }[]
}) => {
  return (
    <Box display="flex" alignItems="center" justifyContent="center" gap={10} px={10}>
      <Typography
        variant="body.md"
        sx={(theme) => ({
          color: theme.palette["primary.800"].main,
          fontSize: "16px !important",
          fontWeight: 600,
          textTransform: "uppercase",
          lineHeight: "140%",
        })}
      >
        {type} sponsors
      </Typography>
      {sponsors.map((sponsor) => (
        <Box key={sponsor.id} position="relative">
          <StyledLink href={sponsor.url} target="_blank">
            <SvgIcon variant={sponsor.icon} size={100} />
          </StyledLink>
        </Box>
      ))}
    </Box>
  )
}

const StyledLink = styled("a")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
}))
